import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { downloadInvoicePDF } from '../utils/pdfGenerator';
import { formatCurrency, getCurrencySymbol } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';

const InvoiceBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'USD',
    tax: 0,
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }]
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchClients();
    if (id) {
      fetchInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClients = async () => {
    try {
      const res = await apiClient.get('/clients');
      setClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchInvoice = async () => {
    try {
      const res = await apiClient.get(`/invoices/${id}`);
      const invoice = res.data;
      setFormData({
        client_id: invoice.client_id,
        issue_date: invoice.issue_date,
        due_date: invoice.due_date,
        currency: invoice.currency,
        tax: invoice.tax,
        notes: invoice.notes || '',
        items: invoice.items
      });
    } catch (error) {
      console.error('Error fetching invoice:', error);
    }
  };

  const handleAddItem = () => {
    // Check if any existing item has a 0 amount/quantity before allowing a new one
    const hasZeroItem = formData.items.some(item => item.quantity <= 0 || item.unit_price <= 0);
    if (hasZeroItem) {
      setErrors({ ...errors, items: 'Please fill out existing line items with quantities and prices > 0 first.' });
      return;
    }

    const newErrors = { ...errors };
    delete newErrors.items;
    setErrors(newErrors);
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'description') {
      newItems[index][field] = value;
    } else {
      // Prevent negative values for quantity and price
      const numValue = parseFloat(value);
      newItems[index][field] = isNaN(numValue) ? '' : Math.max(0, numValue);
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal + (subtotal * formData.tax / 100);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_id) newErrors.client_id = 'Please select a client';
    if (!formData.due_date) newErrors.due_date = 'Please select a Due Date';
    if (formData.issue_date && formData.due_date && new Date(formData.due_date) < new Date(formData.issue_date)) {
      newErrors.due_date = 'Due Date cannot be earlier than Issue Date';
    }
    if (calculateTotal() <= 0) newErrors.total = 'Total amount must be greater than zero';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      if (id) {
        await apiClient.put(`/invoices/${id}`, formData);
      } else {
        await apiClient.post('/invoices', formData);
      }
      navigate('/invoices');
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Failed to save invoice' });
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      let invoiceId = id;
      if (id) {
        // Update the existing invoice with the latest form data first
        await apiClient.put(`/invoices/${id}`, formData);
      } else {
        const res = await apiClient.post('/invoices', formData);
        invoiceId = res.data.id;
      }

      await apiClient.post(`/invoices/${invoiceId}/send`);
      navigate('/invoices');
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Failed to send invoice' });
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="invoice-builder">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900" data-testid="invoice-builder-title">
          {id ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4" data-testid="invoice-error">
          {errors.submit}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Invoice Details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <select
                value={formData.client_id}
                onChange={(e) => {
                  setFormData({ ...formData, client_id: e.target.value });
                  if (errors.client_id) setErrors({ ...errors, client_id: null });
                }}
                data-testid="client-select"
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.client_id ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              {errors.client_id && <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>}
              <button
                onClick={() => navigate('/clients/new')}
                data-testid="add-client-button"
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                + Add new client
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
                <input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  data-testid="issue-date-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                <input
                  type="date"
                  value={formData.due_date}
                  min={formData.issue_date}
                  onChange={(e) => {
                    setFormData({ ...formData, due_date: e.target.value });
                    if (errors.due_date) setErrors({ ...errors, due_date: null });
                  }}
                  data-testid="due-date-input"
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.due_date ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                />
                {errors.due_date && <p className="text-red-500 text-sm mt-1">{errors.due_date}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                data-testid="currency-select"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax (%)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.tax}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setFormData({ ...formData, tax: isNaN(val) ? 0 : Math.max(0, val) });
                }}
                data-testid="tax-input"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                data-testid="notes-input"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Line Items</h2>
          {errors.items && <p className="text-red-500 text-sm mb-4">{errors.items}</p>}

          <div className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded p-4" data-testid={`line-item-${index}`}>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    data-testid={`item-description-${index}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      min="0.01"
                      step="1"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      data-testid={`item-quantity-${index}`}
                      className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      data-testid={`item-price-${index}`}
                      className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded flex items-center justify-end">
                      {formatCurrency(item.quantity * item.unit_price, formData.currency)}
                    </div>
                  </div>
                </div>
                {formData.items.length > 1 && (
                  <button
                    onClick={() => handleRemoveItem(index)}
                    data-testid={`remove-item-${index}`}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleAddItem}
              data-testid="add-item-button"
              className="w-full py-2 border-2 border-dashed border-gray-300 rounded text-gray-600 hover:border-blue-500 hover:text-blue-600"
            >
              + Add Item
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
            {errors.total && <p className="text-red-500 text-sm text-right mb-2">{errors.total}</p>}
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span data-testid="subtotal">{formatCurrency(calculateSubtotal(), formData.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Tax ({formData.tax}%):</span>
              <span data-testid="tax-amount">{formatCurrency(calculateSubtotal() * formData.tax / 100, formData.currency)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total:</span>
              <span data-testid="total">{formatCurrency(calculateTotal(), formData.currency)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-4">
        <button
          onClick={handleSaveDraft}
          disabled={loading}
          data-testid="save-draft-button"
          className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          onClick={handleSendInvoice}
          disabled={loading}
          data-testid="send-invoice-button"
          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'Save & Send'}
        </button>
        <button
          onClick={() => navigate('/invoices')}
          data-testid="cancel-button"
          className="px-6 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InvoiceBuilder;